/**
 * The editor's AI assistant state, kept per editor document OUTSIDE the
 * dialog that shows it: a request keeps running (and lands as one undo
 * step) when the AI dialog is closed, and reopening it shows the live
 * progress, the draft prompt and the session's changes again.
 */

import type { ApolloClient } from '@apollo/client';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { parseObjectDefinitionSpec } from 'shared-types';
import { ObjectDefinitionGenerateDocument, type ObjectDefinitionGenerateMutation, type ObjectDefinitionGenerateMutationVariables } from '../../../types/graphqlTypes';
import { NO_SELECTION, type EditorStore } from '../document/EditorStore';
import { aiErrorCode, pastedImageFile, prepareReferenceImage, type AiErrorCode } from './aiShared';
import { AiProgressTracker } from './aiProgressTracker';
import type { ReferenceImageState } from './ReferenceImageField';

export interface AiChange {
  id: number;
  prompt: string;
  summary: string;
  hadImage: boolean;
  /** The draft was edited while the AI worked: those edits sit one undo step back. */
  overwroteEdits: boolean;
}

export interface AiAssistState {
  prompt: string;
  /** Reference image (data URL ready to send), or null. */
  image: string | null;
  imageUnreadable: boolean;
  imageReading: boolean;
  error: AiErrorCode | null;
  /** Newest first. */
  changes: readonly AiChange[];
  running: boolean;
}

const INITIAL: AiAssistState = { prompt: '', image: null, imageUnreadable: false, imageReading: false, error: null, changes: [], running: false };

export class AiAssistSession {
  readonly tracker = new AiProgressTracker();
  private state: AiAssistState = INITIAL;
  private readonly listeners = new Set<() => void>();
  private nextChangeId = 1;

  constructor(private readonly store: EditorStore) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AiAssistState => this.state;

  private patch(update: Partial<AiAssistState>): void {
    this.state = { ...this.state, ...update };
    for (const listener of this.listeners) listener();
  }

  setPrompt = (prompt: string): void => this.patch({ prompt });

  pickImage = async (file: File): Promise<void> => {
    this.patch({ imageReading: true });
    const prepared = await prepareReferenceImage(file);
    this.patch({ imageReading: false, imageUnreadable: prepared === null, ...(prepared ? { image: prepared } : {}) });
  };

  clearImage = (): void => this.patch({ image: null, imageUnreadable: false });

  /** Sends the prompt (and image) with the current draft; the result replaces the draft as one undo step. */
  async apply(client: ApolloClient, organisationId: string): Promise<void> {
    const request = this.state.prompt.trim();
    if (!request || this.state.running) return;
    const image = this.state.image;
    const { spec, revision } = this.store.getState();
    this.patch({ running: true, error: null });
    try {
      const progressChannel = await this.tracker.begin();
      const variables: ObjectDefinitionGenerateMutationVariables = { organisationId, prompt: request, spec, image, progressChannel };
      const result = await client.mutate<ObjectDefinitionGenerateMutation, ObjectDefinitionGenerateMutationVariables>({ mutation: ObjectDefinitionGenerateDocument, variables });
      const generated = result.data?.objectDefinitionGenerate;
      const parsed = parseObjectDefinitionSpec(generated?.spec);
      if (!generated || !('spec' in parsed)) {
        this.patch({ error: 'AI_INVALID_SPEC' });
        return;
      }
      const overwroteEdits = this.store.getState().revision !== revision;
      this.store.apply(() => parsed.spec, NO_SELECTION);
      const change: AiChange = { id: this.nextChangeId++, prompt: request, summary: generated.summary, hadImage: image !== null, overwroteEdits };
      // A prompt typed while the AI worked is kept; the one just sent is cleared.
      this.patch({ changes: [change, ...this.state.changes], ...(this.state.prompt.trim() === request ? { prompt: '' } : {}), ...(this.state.image === image ? { image: null, imageUnreadable: false } : {}) });
    } catch (e) {
      this.patch({ error: aiErrorCode(e) });
    } finally {
      this.tracker.end();
      this.patch({ running: false });
    }
  }
}

const sessions = new WeakMap<EditorStore, AiAssistSession>();

/** The AI session of an editor document (created on first use, lives as long as the document). */
export function aiAssistSessionFor(store: EditorStore): AiAssistSession {
  let session = sessions.get(store);
  if (!session) {
    session = new AiAssistSession(store);
    sessions.set(store, session);
  }
  return session;
}

export function useAiAssistState(session: AiAssistSession): AiAssistState {
  return useSyncExternalStore(session.subscribe, session.getSnapshot);
}

/** The session's reference image, in the shape ReferenceImageField takes. */
export function useSessionReferenceImage(session: AiAssistSession, state: AiAssistState): ReferenceImageState {
  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      const file = pastedImageFile(event.clipboardData);
      if (!file) return;
      event.preventDefault();
      void session.pickImage(file);
    },
    [session],
  );
  return useMemo(
    () => ({ image: state.image, unreadable: state.imageUnreadable, reading: state.imageReading, pick: session.pickImage, clear: session.clearImage, onPaste }),
    [state.image, state.imageUnreadable, state.imageReading, session, onPaste],
  );
}
